import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button/standard-button';
import Modal, { ModalBody, ModalFooter, ModalTransition } from '@atlaskit/modal-dialog';
import { Radio } from '@atlaskit/radio';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { CHANGE_TEMPLATE_MODAL } from '../../../../common/constants';
import type { TemplateType } from '../../../../common/constants/project';
import type { Template } from '../../../../common/constants/steps';
import messages from './messages';
import { TemplateCard } from './template-card';

type Props = {
	isOpen: boolean;
	templates: Template[];
	onClose: () => void;
	onConfirm: (template: TemplateType) => void;
	defaultTemplate: TemplateType;
};

export const TemplateModal = ({
	templates,
	defaultTemplate,
	isOpen,
	onClose,
	onConfirm,
}: Props) => {
	const { formatMessage } = useIntl();
	const [value, setValue] = useState(defaultTemplate);

	const getTemplates = useCallback(
		() =>
			templates.map(({ key, iconUrl, title, description }) => (
				<RadioContainer key={key} onClick={() => setValue(key)}>
					<Radio label="" isChecked={key === value} />
					<TemplateCardWrapper>
						<TemplateCard
							iconUrl={iconUrl}
							title={<Title>{formatMessage(title)}</Title>}
							description={<Description>{formatMessage(description)}</Description>}
							showOutline={key === value}
							enableHover
						/>
					</TemplateCardWrapper>
				</RadioContainer>
			)),
		[formatMessage, templates, value],
	);

	return (
		<ContextualAnalyticsData sourceName={CHANGE_TEMPLATE_MODAL} sourceType={MODAL}>
			<ModalTransition>
				{isOpen && (
					<ShortcutScope>
						<Modal
							onClose={onClose}
							width="large"
							testId="project-create-importer-upload-flow.ui.project-create-screen.templates.template-modal"
						>
							<ModalContent>
								<Heading>{formatMessage(messages.heading)}</Heading>
								<ModalBody>{getTemplates()}</ModalBody>
								<ModalFooter>
									<Button appearance="subtle" onClick={onClose}>
										{formatMessage(messages.cancel)}
									</Button>
									<Button
										testId="project-create-importer-upload-flow.ui.project-create-screen.templates.template-modal.button"
										appearance="primary"
										onClick={() => onConfirm(value)}
									>
										{formatMessage(messages.confirm)}
									</Button>
								</ModalFooter>
							</ModalContent>
						</Modal>
						<FireScreenAnalytics />
					</ShortcutScope>
				)}
			</ModalTransition>
		</ContextualAnalyticsData>
	);
};

export { TemplateCard };

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RadioContainer = styled.button({
	display: 'flex',
	border: 'none',
	backgroundColor: 'transparent',
	width: '100%',
	cursor: 'pointer',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	outline: 'none !important',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Heading = styled.div({
	padding: token('space.300', '24px'),
	font: token('font.heading.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
	fontStyle: 'normal',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TemplateCardWrapper = styled.div({
	display: 'flex',
	width: '100%',
	margin: `0 ${token('space.150', '12px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ModalContent = styled.div({
	display: 'flex',
	flexDirection: 'column',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	borderRadius: '3px',
	width: 'calc(100vw - 48px)',
	maxWidth: '800px',
	boxShadow: token(
		'elevation.shadow.overlay',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0px 8px 12px ${colors.N40A}, 0px 0px 1px ${colors.N60A}`,
	),
	position: 'absolute',
	// Using space at the top to keep the modal at the top of the content and below the progressbar
	// Could have use center aligned but not supported by the modal dialog yet
	top: token('space.1000', '80px'),
	marginTop: token('space.500', '40px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	font: token('font.heading.xsmall'),
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N800),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.div({
	font: token('font.body'),
	textAlign: 'left',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N300),
});
